import { DetailsList, IColumn, IDropdownOption, SelectionMode, Stack, StackItem, TooltipOverflowMode } from "@fluentui/react";
import React, {useEffect, useState} from "react";
import Pagination from "../../Pagination/Pagination";
import {WorkflowHistoryItem} from "../../../model/WorkflowHistoryItem";
import {TooltipHost} from "@fluentui/react/lib/Tooltip";

interface ViewRequestsState {
    currentPage: number,
    itemsPerPage: number,
    columns: IColumn[],
    items: WorkflowHistoryItem[],
}

export interface ExtendedWorkflowHistoryItem extends WorkflowHistoryItem {
    dateValue: Date;
    formattedDate: string;
}

function DetailsListWithPagination(props: { items: WorkflowHistoryItem[], styleStatus: any }) {

    const columns: IColumn[] = [
        {
            key: 'date',
            name: 'Date/time',
            fieldName: 'dateValue',
            data: 'number',
            minWidth: 100,
            maxWidth: 221,
            isResizable: true,
            isSorted: true,
            isSortedDescending: false,
            onColumnClick: _onColumnClick,
            onRender: (item: ExtendedWorkflowHistoryItem) => {
                return <span>{item.formattedDate}</span>
            }
        },
        {
            key: 'actor',
            name: 'Actor',
            fieldName: 'updatedBy',
            minWidth: 100,
            maxWidth: 191,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: 'action',
            name: 'Action',
            fieldName: 'actionTypeName',
            minWidth: 100,
            maxWidth: 135,
            isResizable: true,
            onColumnClick: _onColumnClick
        },
        {
            key: 'status',
            name: 'Status',
            fieldName: 'statusTypeId',
            minWidth: 100,
            maxWidth: 225,
            isResizable: true,
            onColumnClick: _onColumnClick,
            onRender: (item: ExtendedWorkflowHistoryItem) => {
                return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={props.styleStatus(item.statusTypeId)}>{props.styleStatus(item.statusTypeId)}</TooltipHost>
            }
        }
    ];

    const unSortedItems: ExtendedWorkflowHistoryItem[] = props.items.map(item => {
        const date = new Date(item.updateDate);
        return {
            ...item,
            dateValue: date,
            formattedDate: `${date.toLocaleDateString('fr-BE')} ${date.toLocaleTimeString('fr-BE', {hour: '2-digit', minute:'2-digit'})}`,
        };
    });

    const sorteditems = [...unSortedItems].sort((a, b) => a.dateValue.getTime() - b.dateValue.getTime());

    function _onColumnClick(event: React.MouseEvent<HTMLElement>, column: IColumn) {
        let isSortedDescending = column.isSortedDescending;

        if (column.isSorted) {
            isSortedDescending = !isSortedDescending;
        }

        let allSortedItems = _copyAndSort(sorteditems, column.fieldName!, isSortedDescending);
        setAllSortedItems(allSortedItems);
        setSortedColumns(columns.map(col => {
            col.isSorted = col.key === column.key;

            if (col.isSorted) {
                col.isSortedDescending = isSortedDescending;
            }
            return col;
        }));
    }

    const [allSortedItems, setAllSortedItems] = useState<ExtendedWorkflowHistoryItem[]>(sorteditems);
    const [sortedColumns, setSortedColumns] = useState<IColumn[]>(columns);
    const ITEMS_PER_PAGE: number = 10;
    const INIT_PAGE: number = 1;
    const [modalState, setModalState] = React.useState<ViewRequestsState>({
        currentPage: INIT_PAGE,
        itemsPerPage: ITEMS_PER_PAGE,
        columns: columns,
        items: getNextPage(ITEMS_PER_PAGE, INIT_PAGE),
    });

    useEffect(() => {
        let newSortItems = getNextPage(modalState.itemsPerPage, INIT_PAGE, allSortedItems);
        setModalState({
            ...modalState,
            currentPage: INIT_PAGE,
            items: newSortItems,
            columns: sortedColumns,
        });
    }, [allSortedItems, sortedColumns]);

    function _copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
        const key = columnKey as keyof T;
        return items.slice(0).sort((a: T, b: T) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
    }

    function _onPageChange(selectedItem: number) {
        const nextPage = getNextPage(modalState.itemsPerPage, selectedItem);
        setModalState({
            ...modalState,
            items: nextPage,
            currentPage: selectedItem,
        });
    }

    function _onChangeItemsPerPage(event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) {
        setModalState({
            ...modalState,
            items: getNextPage(item.key as number, INIT_PAGE),
            itemsPerPage: item.key as number,
            currentPage: 1,
        });
    }
    function getNextPage(itemsPerPage: number, page: number, itemsSorted: ExtendedWorkflowHistoryItem[] = sorteditems) {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        if (!itemsSorted.length) {
            return [];
        }
        return itemsSorted.slice(startIndex, endIndex);
    }

    const addTooltipToColumnHeader = (cols: IColumn[]) => {
        cols.forEach(col => {
            col.onRenderHeader= (props) => {
                return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={props?.column.name}>{props?.column.name}</TooltipHost>
            }
        });
        return cols;
    };

    function _renderItemColumn(item?: any, index?: number, column?: IColumn) {
        if(item && column) {
            const fieldContent = item[column.fieldName as keyof any] as string;
            return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={fieldContent}>{fieldContent}</TooltipHost>
        }
    }

    return (
        <Stack>
            <StackItem>
                <DetailsList
                    selectionMode={SelectionMode.none}
                    layoutMode={1}
                    columns={addTooltipToColumnHeader(modalState.columns)}
                    onRenderItemColumn={_renderItemColumn}
                    items={modalState.items}
                />
            </StackItem>
            <StackItem>
                <Pagination
                    currentPage={modalState.currentPage}
                    onPageChange={_onPageChange}
                    itemsPerPage={modalState.itemsPerPage}
                    onChangeItemsPerPage={_onChangeItemsPerPage}
                    totalItems={props.items.length}
                />
            </StackItem>
        </Stack>
    );
}
export default DetailsListWithPagination;